import ValidateModule from "./ValidateModule";

import type { IEventChannel, ApiOpts } from "../model/Models";
import type AjaxRequestBroker from "../AjaxRequestBroker";
import type CacheModule from "./CacheModule";

const $: JQueryStatic = global.$;
const es = global.es;

/**
 * @class StatusModule
 */
export default class StatusModule {
    private eventChannel: IEventChannel;
    private rqBroker: AjaxRequestBroker;
    private resourceName: string;
    private cache: CacheModule;

    /**
     * 
     * @param eventChannel 
     * @param rqBroker 
     * @param resourceName 
     * @param cache 
     */
    constructor(eventChannel: IEventChannel, rqBroker: AjaxRequestBroker, resourceName: string, cache: CacheModule) {
        this.eventChannel = eventChannel;
        this.rqBroker = rqBroker;
        this.resourceName = resourceName;
        this.cache = cache;

        // TODO FIXME Does this even make sense nowadays?
        eventChannel.addListener("onDestroy", () => {
            // prevent memory leaks when the map widget is destroyed
            this.eventChannel = this.rqBroker = this.resourceName = this.cache = null;
        });

        this.getStatus = this.getStatus.bind(this);
        this.cancelTask = this.cancelTask.bind(this);
    }

    /**
     * getStatus
     * 
     * @param id 
     * @param opts 
     * 
     * @returns 
     */
    getStatus(id: string, opts?: ApiOpts) {
        if (!ValidateModule.checkMandatoryArg(id)) {
            return es.util.deferred.rejected();
        }

        opts = opts || {};
        const url = es.api.getURL(this.resourceName) + '/' + id;

        return this.rqBroker.doGet({
            'url': url,
            opts: opts
        }).then((rs) => {
            if (rs.percentage < 0) {
                this.cache?.clear('AsyncTasks');
                return es.util.deferred.rejected(rs);
            }
            
            if (rs.percentage < 100) {
                this.cache?.clear('AsyncTasks');
                return rs;
            }
            
            // rs.percentage === 100
            this.cache?.clearAll();

            if (rs.$type == 'TerritoriesFromOptimizerStatus' || rs.$type == 'AsyncTerritoryDistanceMatrixOptimizerStatus' || rs.$type == 'DriveTimeCreateTerritoryStatus') { // OptimizeStatus/DriveTimeStatus... No changes done!
                return rs;
            }
            
            if (!rs.layerViews && !rs.layerView) { // !AnalysisStatus
                return rs;
            }
            
            var layerViews = rs.layerViews || [rs.layerView];
            
            // TODO: fetch these with array of ids
            var lviDfds = [];
            for (var i = 0; i < layerViews.length; i++) {
                this.cache?.setEntity("LayerView", layerViews[i]);
                
                this.cache?.clear("LayerInfo_LayerView", layerViews[i].id);
                this.cache?.clear("LayerView_LayerInfo", layerViews[i].id);
                this.cache?.clear("LayerInfo", layerViews[i].id);
                
                lviDfds.push(es.api.layerInfo.getForLayerView(layerViews[i].id));
            }

            return es.api.geoMap.get(layerViews[0].geoMapId).then((geoMap) => {
                return Promise.all(lviDfds).then((layerInfos) => {
                    if (this.eventChannel) {
                        if (rs.analysis) {
                            return es.api.appResources.setWorkingCopyChanged(geoMap.id).then(() => { // TODO FIXME Shouldn't this be done server-side?
                                for (let i = 0; i < rs.layerViews.length; i++) {
                                    this.eventChannel?.fireEvent("onUpdateLayerView", rs.layerViews[i], layerInfos[i], geoMap.lastModified);
                                }
                                
                                // TODO FIXME How to detect if create or update?!?! In reality we don't use either event as we reload page...
                                // this.eventChannel?.fireEvent("onCreateAnalysis", rs.analysis);
                                // this.eventChannel?.fireEvent("onUpdateAnalysis", rs.analysis);
                                
                                return rs;
                            });
                        }
                        
                        if (rs.$type == 'WorkIndexStatus') {
                            for (let i = 0; i < layerViews.length; i++) {
                                this.eventChannel.fireEvent("onUpdateLayerView", layerViews[i], layerInfos[i], geoMap.lastModified);
                            }
                            
                            if (rs.editDerivedAttributeResult) {
                                this.eventChannel.fireEvent("onEditTerritoryDerivedAttribute", rs.editDerivedAttributeResult);
                            } else {
                                this.eventChannel.fireEvent("onCalculateWorkIndex");
                            }
                            
                            return rs;
                        }
                        
                        // If we're here, it's BufferStatus... only one layerView... right??!! TODO FIXME Could be something else?!?!
                        const layerInfo = layerInfos[0];
                        
                        // Convert date attr values to Date
                        layerInfo.attributes.filter(function(a) {
                            return a.attrType == 'TIMESTAMP';
                        }).map(function(a) {
                            return a.name;
                        }).forEach(function(attrName) {
                            rs.attributesList.forEach(function(al) {
                                if (al[attrName]) {
                                    al[attrName] = new Date(al[attrName]);
                                }
                            });
                        });
                        
                        this.eventChannel.fireEvent("onBufferComplete", rs, geoMap.lastModified);
                        this.eventChannel.fireEvent("onUpdateLayerView", rs.layerView, layerInfo, geoMap.lastModified);
                        this.eventChannel.fireEvent("onBatchEditFeatures", rs.editedFeatureCount, rs.layerView.id, true, rs.lastModified || layerInfo.featureInfo.lastModified, rs.attributesList);
                    }
                    
                    return rs;
                });
            });
        });
    }
    
    /**
     * cancelTask
     * 
     * @param taskId 
     * @param opts 
     * 
     * @returns 
     */
    cancelTask(taskId: string, opts?: ApiOpts) {
        if (!ValidateModule.checkMandatoryArg(taskId)) {
            return es.util.deferred.rejected();
        }
        
        const url = es.api.getURL(this.resourceName) + '/' + taskId;

        return this.rqBroker.doDelete({
            'url': url,
            'opts': opts
        });
    }
}
